import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Divider,
  Paper,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import MemoryIcon from '@mui/icons-material/Memory';
import StorageIcon from '@mui/icons-material/Storage';
import TimerIcon from '@mui/icons-material/Timer';
import SpeedIcon from '@mui/icons-material/Speed';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import BugReportIcon from '@mui/icons-material/BugReport';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import { apiClient } from '../../framework/api/api.client';
import { AgenticFeatureRegistry } from '../ai/chat/AgenticFeatureRegistry';
import { useAgenticContext } from '../ai/chat/hooks/useAgenticContext';
import { 
  initializeMemoryAnalysisProvider, 
  cleanupMemoryAnalysisProvider,
  setMemoryAnalysisFocus 
} from './MemoryAnalysisFeatureProvider';

// Types
interface MemoryStats {
  heapUsed: string;
  heapTotal: string;
  rss: string;
  external: string;
  arrayBuffers: string;
  heapPercentUsed: number;
  v8HeapLimit: string;
  gcAvailable: boolean;
}

interface InstanceInfo {
  instanceId: string;
  podName: string | null;
  namespace: string | null;
  nodeName: string | null;
  containerId: string | null;
  ipAddress: string | null;
}

interface SystemMetrics {
  instance?: InstanceInfo;
  memory: {
    heap: {
      used: string;
      total: string;
      usedBytes: number;
      totalBytes: number;
      percentUsed: number;
    };
    rss: string;
    external: string;
    arrayBuffers: string;
    v8HeapLimit: string;
  };
  process: {
    uptime: string;
    uptimeSeconds: number;
    pid: number;
    nodeVersion: string;
    platform: string;
    arch: string;
  };
  system: {
    totalMemory: string;
    freeMemory: string;
    cpuCount: number;
    loadAverage: number[];
  };
  gc: {
    available: boolean;
    lastManualGC: string | null;
  };
}

interface MemorySnapshot {
  timestamp: string;
  heapUsedMB: number;
  heapTotalMB: number;
  rssMB: number;
  externalMB: number;
  arrayBuffersMB: number;
}

interface MemoryHistory {
  count: number;
  snapshots: MemorySnapshot[];
  trend?: {
    firstSnapshot: MemorySnapshot | null;
    lastSnapshot: MemorySnapshot | null;
    heapGrowthMB: number;
    durationMinutes: number;
  };
}

interface GCResult {
  success: boolean;
  message: string;
  before?: { heapUsed: string; heapTotal: string };
  after?: { heapUsed: string; heapTotal: string };
  freed?: string;
}

// Heap Analysis Types
interface HeapSpaceInfo {
  spaceName: string;
  spaceSize: number;
  spaceUsedSize: number;
  spaceAvailableSize: number;
  physicalSpaceSize: number;
  spaceSizeMB: number;
  spaceUsedSizeMB: number;
  utilizationPercent: number;
}

interface HeapAnalysis {
  timestamp: string;
  heapStatistics: {
    totalHeapSize: number;
    totalHeapSizeExecutable: number;
    totalPhysicalSize: number;
    totalAvailableSize: number;
    usedHeapSize: number;
    heapSizeLimit: number;
    mallocedMemory: number;
    peakMallocedMemory: number;
    doesZapGarbage: boolean;
    numberOfNativeContexts: number;
    numberOfDetachedContexts: number;
    totalHeapSizeMB: number;
    usedHeapSizeMB: number;
    heapSizeLimitMB: number;
    availableMB: number;
    utilizationPercent: number;
  };
  heapSpaces: HeapSpaceInfo[];
  processMemory: {
    rss: number;
    heapTotal: number;
    heapUsed: number;
    external: number;
    arrayBuffers: number;
    rssMB: number;
    externalMB: number;
    arrayBuffersMB: number;
  };
  analysis: {
    memoryPressure: 'low' | 'medium' | 'high' | 'critical';
    recommendations: string[];
    potentialIssues: string[];
  };
}

interface HeapSnapshotResult {
  success: boolean;
  message: string;
  filename?: string;
  filepath?: string;
  sizeMB?: number;
  timestamp?: string;
  instructions?: string;
}

interface EnvironmentCapabilities {
  healthMetrics: boolean;
  gcControl: boolean;
  clusterMetrics: boolean;
  prometheusMock?: boolean;
  prometheus?: {
    configured: boolean;
    url?: string;
  };
  kubernetes: {
    detected: boolean;
    namespace?: string;
  };
}

interface PodMetrics {
  podName: string;
  namespace: string;
  // Memory metrics
  memoryUsedBytes: number;
  memoryUsedMB: number;
  memoryLimitMB?: number;
  memoryPercentUsed?: number;
  // CPU metrics
  cpuUsageMillicores?: number;
  cpuLimitMillicores?: number;
  cpuPercentUsed?: number;
  // Health metrics
  restartCount?: number;
  ready?: boolean;
  // Network metrics (bytes per second)
  networkRxBytesPerSec?: number;
  networkTxBytesPerSec?: number;
}

interface ClusterMetrics {
  timestamp: string;
  querySource: 'prometheus' | 'mock';
  pods: PodMetrics[];
  summary: {
    podCount: number;
    // Memory summary
    totalMemoryMB: number;
    avgMemoryMB: number;
    maxMemoryMB: number;
    minMemoryMB: number;
    // CPU summary
    totalCpuMillicores?: number;
    avgCpuMillicores?: number;
    // Health summary
    totalRestarts?: number;
    podsReady?: number;
    podsWithHighMemory?: number;
    // Network summary
    totalNetworkRxBytesPerSec?: number;
    totalNetworkTxBytesPerSec?: number;
  };
  currentPod?: string;
}

// Styled components
const MetricCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: theme.spacing(1.5),
}));

const MetricValue = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 700,
  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
  background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: 'rgba(255, 255, 255, 0.6)',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  marginBottom: theme.spacing(0.5),
}));

const ChartCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.8) 100%)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: theme.spacing(2),
}));

const StatusChip = styled(Chip)<{ status: 'healthy' | 'warning' | 'critical' }>(({ theme, status }) => ({
  fontWeight: 600,
  ...(status === 'healthy' && {
    backgroundColor: 'rgba(34, 197, 94, 0.2)',
    color: '#22c55e',
    border: '1px solid rgba(34, 197, 94, 0.3)',
  }),
  ...(status === 'warning' && {
    backgroundColor: 'rgba(245, 158, 11, 0.2)',
    color: '#f59e0b',
    border: '1px solid rgba(245, 158, 11, 0.3)',
  }),
  ...(status === 'critical' && {
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    color: '#ef4444',
    border: '1px solid rgba(239, 68, 68, 0.3)',
  }),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: theme.spacing(1),
  fontWeight: 600,
}));

// Helper function to format bytes per second
const formatBytesPerSec = (bytesPerSec: number): string => {
  if (bytesPerSec < 1024) return `${bytesPerSec} B/s`;
  if (bytesPerSec < 1024 * 1024) return `${(bytesPerSec / 1024).toFixed(1)} KB/s`;
  return `${(bytesPerSec / 1024 / 1024).toFixed(1)} MB/s`;
};

// Chart colors
const CHART_COLORS = {
  heapUsed: '#60a5fa',
  heapTotal: '#a78bfa',
  rss: '#34d399',
  external: '#fbbf24',
  cpu: '#f472b6',
  network: '#38bdf8',
};

// Heap space colors for visualization
const HEAP_SPACE_COLORS: Record<string, string> = {
  new_space: '#60a5fa',
  old_space: '#f472b6',
  code_space: '#a78bfa',
  large_object_space: '#fbbf24',
  map_space: '#34d399',
  code_large_object_space: '#fb923c',
  new_large_object_space: '#38bdf8',
  shared_space: '#e879f9',
  trusted_space: '#22d3ee',
};

export const EnvironmentMonitor: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [systemMetrics, setSystemMetrics] = useState<SystemMetrics | null>(null);
  const [memoryHistory, setMemoryHistory] = useState<MemorySnapshot[]>([]);
  
  // Cluster-wide monitoring state
  const [capabilities, setCapabilities] = useState<EnvironmentCapabilities | null>(null);
  const [clusterMetrics, setClusterMetrics] = useState<ClusterMetrics | null>(null);
  const [clusterLoading, setClusterLoading] = useState(false);
  const [clusterError, setClusterError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'pod' | 'cluster'>('pod');
  const [isPolling, setIsPolling] = useState(false);
  const [gcResult, setGcResult] = useState<GCResult | null>(null);
  const [autoSnapshot, setAutoSnapshot] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const snapshotIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Heap analysis state
  const [heapAnalysis, setHeapAnalysis] = useState<HeapAnalysis | null>(null);
  const [heapAnalysisLoading, setHeapAnalysisLoading] = useState(false);
  const [heapSnapshotResult, setHeapSnapshotResult] = useState<HeapSnapshotResult | null>(null);
  const [heapSnapshotLoading, setHeapSnapshotLoading] = useState(false);

  // Fetch system metrics
  const fetchSystemMetrics = useCallback(async () => {
    try {
      const metrics = await apiClient.get<SystemMetrics>('/health/system');
      setSystemMetrics(metrics);
      setError(null);
    } catch (err) {
      setError('Failed to fetch system metrics');
      console.error('Error fetching system metrics:', err);
    }
  }, []);

  // Fetch memory history
  const fetchMemoryHistory = useCallback(async () => {
    try {
      const history = await apiClient.get<MemoryHistory>('/health/memory/history');
      setMemoryHistory(history.snapshots || []);
    } catch (err) {
      console.error('Error fetching memory history:', err);
    }
  }, []);

  // Record a snapshot
  const recordSnapshot = useCallback(async () => {
    try {
      await apiClient.post('/health/memory/snapshot');
      await fetchMemoryHistory();
    } catch (err) {
      console.error('Error recording snapshot:', err);
    }
  }, [fetchMemoryHistory]);

  // Trigger garbage collection
  const triggerGC = useCallback(async (full: boolean = false) => {
    try {
      const result = await apiClient.post<GCResult>(`/health/gc?full=${full}`);
      setGcResult(result);
      await fetchSystemMetrics();
      setTimeout(() => setGcResult(null), 5000);
    } catch (err) {
      setGcResult({ success: false, message: 'Failed to trigger GC' });
      console.error('Error triggering GC:', err);
    }
  }, [fetchSystemMetrics]);

  // Fetch environment capabilities
  const fetchCapabilities = useCallback(async () => {
    try {
      const caps = await apiClient.get<EnvironmentCapabilities>('/health/capabilities');
      setCapabilities(caps);
      // Auto-switch to cluster view if cluster metrics are available
      if (caps.clusterMetrics && caps.kubernetes.detected) {
        // Keep current view, but enable the option
      }
    } catch (err) {
      console.error('Error fetching capabilities:', err);
    }
  }, []);

  // Fetch cluster-wide metrics (from Prometheus)
  const fetchClusterMetrics = useCallback(async () => {
    if (!capabilities?.clusterMetrics) return;
    
    setClusterLoading(true);
    setClusterError(null);
    
    try {
      const metrics = await apiClient.get<ClusterMetrics | { error: string; message: string }>('/health/cluster');
      
      if ('error' in metrics) {
        setClusterError(metrics.message);
        setClusterMetrics(null);
      } else {
        setClusterMetrics(metrics);
      }
    } catch (err) {
      setClusterError('Failed to fetch cluster metrics');
      console.error('Error fetching cluster metrics:', err);
    } finally {
      setClusterLoading(false);
    }
  }, [capabilities?.clusterMetrics]);

  // Clear history
  const clearHistory = useCallback(async () => {
    try {
      await apiClient.post('/health/memory/history/clear');
      setMemoryHistory([]);
    } catch (err) {
      console.error('Error clearing history:', err);
    }
  }, []);

  // Fetch heap analysis
  const fetchHeapAnalysis = useCallback(async () => {
    setHeapAnalysisLoading(true);
    // Set focus to trigger AI context for heap analysis
    setMemoryAnalysisFocus('heap-analysis');
    try {
      const analysis = await apiClient.get<HeapAnalysis>('/health/heap/analysis');
      setHeapAnalysis(analysis);
    } catch (err) {
      console.error('Error fetching heap analysis:', err);
    } finally {
      setHeapAnalysisLoading(false);
    }
  }, []);

  // Write heap snapshot
  const writeHeapSnapshot = useCallback(async () => {
    setHeapSnapshotLoading(true);
    setHeapSnapshotResult(null);
    try {
      const result = await apiClient.post<HeapSnapshotResult>('/health/heap/snapshot');
      setHeapSnapshotResult(result);
      setTimeout(() => setHeapSnapshotResult(null), 15000); // Clear after 15s
    } catch (err) {
      setHeapSnapshotResult({ success: false, message: 'Failed to write heap snapshot' });
      console.error('Error writing heap snapshot:', err);
    } finally {
      setHeapSnapshotLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      await Promise.all([fetchSystemMetrics(), fetchMemoryHistory(), fetchCapabilities()]);
      setLoading(false);
    };
    initialize();
  }, [fetchSystemMetrics, fetchMemoryHistory, fetchCapabilities]);

  // Register Memory Analysis AI feature context when component mounts
  useEffect(() => {
    console.log('🧠 [Environment Monitor] Initializing Memory Analysis AI feature context...');
    
    // Initialize the memory analysis provider (registers event listeners and prompts)
    initializeMemoryAnalysisProvider();
    
    return () => {
      cleanupMemoryAnalysisProvider();
    };
  }, []);
  
  // Note: Feature context is now managed by route-based context in App.tsx
  // This ensures context is automatically cleared when navigating away

  // Fetch cluster metrics when capabilities are available and view mode is cluster
  useEffect(() => {
    if (capabilities?.clusterMetrics && viewMode === 'cluster') {
      fetchClusterMetrics();
    }
  }, [capabilities?.clusterMetrics, viewMode, fetchClusterMetrics]);

  // Polling for live updates (includes cluster metrics in cluster view)
  useEffect(() => {
    if (isPolling) {
      pollingIntervalRef.current = setInterval(() => {
        fetchSystemMetrics();
        if (viewMode === 'cluster' && capabilities?.clusterMetrics) {
          fetchClusterMetrics();
        }
      }, 3000);
    } else {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [isPolling, fetchSystemMetrics, fetchClusterMetrics, viewMode, capabilities?.clusterMetrics]);

  // Auto-snapshot
  useEffect(() => {
    if (autoSnapshot) {
      snapshotIntervalRef.current = setInterval(recordSnapshot, 10000);
    } else {
      if (snapshotIntervalRef.current) {
        clearInterval(snapshotIntervalRef.current);
        snapshotIntervalRef.current = null;
      }
    }
    return () => {
      if (snapshotIntervalRef.current) {
        clearInterval(snapshotIntervalRef.current);
      }
    };
  }, [autoSnapshot, recordSnapshot]);

  // Get health status
  const getHealthStatus = (): 'healthy' | 'warning' | 'critical' => {
    if (!systemMetrics) return 'healthy';
    const percentUsed = systemMetrics.memory.heap.percentUsed;
    if (percentUsed >= 90) return 'critical';
    if (percentUsed >= 70) return 'warning';
    return 'healthy';
  };

  // Set AI focus when memory is critical (triggers contextual AI prompt)
  useEffect(() => {
    const status = getHealthStatus();
    if (status === 'critical') {
      console.log('⚠️ [Environment Monitor] Critical memory detected - setting AI focus');
      setMemoryAnalysisFocus('high-memory-alert');
    }
  }, [systemMetrics?.memory?.heap?.percentUsed]);

  // Format chart data
  const chartData = memoryHistory.map((snapshot) => ({
    time: new Date(snapshot.timestamp).toLocaleTimeString(),
    'Heap Used': snapshot.heapUsedMB,
    'Heap Total': snapshot.heapTotalMB,
    'RSS': snapshot.rssMB,
  }));

  // Calculate trend
  const getTrend = () => {
    if (memoryHistory.length < 2) return null;
    const first = memoryHistory[0];
    const last = memoryHistory[memoryHistory.length - 1];
    const growth = last.heapUsedMB - first.heapUsedMB;
    const durationMs = new Date(last.timestamp).getTime() - new Date(first.timestamp).getTime();
    const durationMin = Math.round(durationMs / 1000 / 60);
    return { growth, durationMin };
  };

  const trend = getTrend();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  // Get instance display name
  const getInstanceDisplayName = () => {
    if (!systemMetrics?.instance) return 'Unknown Instance';
    const inst = systemMetrics.instance;
    if (inst.podName && inst.namespace) {
      return `${inst.podName} (${inst.namespace})`;
    }
    return inst.podName || inst.instanceId || 'Local';
  };

  // Check if running in K8s
  const isKubernetes = systemMetrics?.instance?.namespace || systemMetrics?.instance?.nodeName;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Environment Health Monitor
          </Typography>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            {/* View mode toggle - only show if cluster metrics available */}
            {capabilities?.clusterMetrics && (
              <Box
                sx={{
                  display: 'flex',
                  backgroundColor: 'rgba(30, 41, 59, 0.8)',
                  borderRadius: 1,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  mr: 2,
                }}
              >
                <Button
                  size="small"
                  onClick={() => setViewMode('pod')}
                  sx={{
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    textTransform: 'none',
                    backgroundColor: viewMode === 'pod' ? 'rgba(96, 165, 250, 0.3)' : 'transparent',
                    color: viewMode === 'pod' ? '#60a5fa' : 'rgba(255, 255, 255, 0.6)',
                    '&:hover': { backgroundColor: 'rgba(96, 165, 250, 0.2)' },
                  }}
                >
                  Pod View
                </Button>
                <Button
                  size="small"
                  onClick={() => setViewMode('cluster')}
                  sx={{
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    textTransform: 'none',
                    backgroundColor: viewMode === 'cluster' ? 'rgba(52, 211, 153, 0.3)' : 'transparent',
                    color: viewMode === 'cluster' ? '#34d399' : 'rgba(255, 255, 255, 0.6)',
                    '&:hover': { backgroundColor: 'rgba(52, 211, 153, 0.2)' },
                  }}
                >
                  Cluster View
                </Button>
              </Box>
            )}
            <Typography variant="body2" color="textSecondary">
              {viewMode === 'cluster' ? 'Viewing cluster:' : 'Monitoring instance:'}
            </Typography>
            <Chip
              size="small"
              label={viewMode === 'cluster' ? (capabilities?.kubernetes.namespace || 'default') : getInstanceDisplayName()}
              sx={{
                backgroundColor: viewMode === 'cluster' ? 'rgba(52, 211, 153, 0.2)' : 'rgba(96, 165, 250, 0.2)',
                color: viewMode === 'cluster' ? '#34d399' : '#60a5fa',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
              }}
            />
            {isKubernetes && viewMode === 'pod' && (
              <Chip
                size="small"
                label="K8s"
                sx={{
                  backgroundColor: 'rgba(52, 211, 153, 0.2)',
                  color: '#34d399',
                  fontSize: '0.7rem',
                }}
              />
            )}
            {capabilities?.clusterMetrics && viewMode === 'cluster' && (
              <Chip
                size="small"
                label={capabilities.prometheusMock ? "Mock Data" : "Prometheus"}
                sx={{
                  backgroundColor: capabilities.prometheusMock 
                    ? 'rgba(168, 85, 247, 0.2)' 
                    : 'rgba(251, 146, 60, 0.2)',
                  color: capabilities.prometheusMock ? '#a855f7' : '#fb923c',
                  fontSize: '0.7rem',
                }}
              />
            )}
            {systemMetrics?.instance?.ipAddress && viewMode === 'pod' && (
              <Typography variant="caption" color="textSecondary" fontFamily="monospace">
                {systemMetrics.instance.ipAddress}
              </Typography>
            )}
          </Box>
          {isKubernetes && viewMode === 'pod' && !capabilities?.clusterMetrics && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
              ⚠️ Multi-pod environment: Metrics shown are for this specific pod only. 
              Set PROMETHEUS_URL to enable cluster-wide view.
            </Typography>
          )}
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <StatusChip
            status={getHealthStatus()}
            label={getHealthStatus().toUpperCase()}
            icon={<MemoryIcon />}
          />
          <Tooltip title="Refresh metrics">
            <IconButton onClick={fetchSystemMetrics} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {gcResult && (
        <Alert severity={gcResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
          {gcResult.message}
          {gcResult.freed && ` Freed: ${gcResult.freed}`}
        </Alert>
      )}

      {/* Cluster View */}
      {viewMode === 'cluster' && capabilities?.clusterMetrics && (
        <Box mb={3}>
          {clusterLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography color="textSecondary">Loading cluster metrics from Prometheus...</Typography>
            </Box>
          )}
          
          {clusterError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {clusterError}
            </Alert>
          )}
          
          {clusterMetrics && !clusterLoading && (
            <>
              {/* Mock Mode Notice */}
              {capabilities?.prometheusMock && (
                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 2, 
                    backgroundColor: 'rgba(168, 85, 247, 0.1)',
                    border: '1px solid rgba(168, 85, 247, 0.3)',
                    '& .MuiAlert-icon': { color: '#a855f7' }
                  }}
                >
                  <Typography variant="body2">
                    <strong>🎭 Mock Mode Active</strong> — Displaying simulated cluster data for development/demo purposes. 
                    Set <code style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '2px 6px', borderRadius: 4 }}>PROMETHEUS_URL</code> to 
                    connect to a real Prometheus instance.
                  </Typography>
                </Alert>
              )}
              
              {/* Cluster Summary - Row 1: Core Metrics */}
              <Grid container spacing={2} mb={2}>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard elevation={0}>
                    <MetricLabel>Pods</MetricLabel>
                    <MetricValue>{clusterMetrics.summary.podCount}</MetricValue>
                    <Typography variant="caption" color="textSecondary">
                      {clusterMetrics.summary.podsReady}/{clusterMetrics.summary.podCount} ready
                    </Typography>
                  </MetricCard>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard elevation={0}>
                    <MetricLabel>Memory</MetricLabel>
                    <MetricValue sx={{ fontSize: '1.4rem' }}>{clusterMetrics.summary.totalMemoryMB} MB</MetricValue>
                    <Typography variant="caption" color="textSecondary">
                      avg: {clusterMetrics.summary.avgMemoryMB} MB
                    </Typography>
                  </MetricCard>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard elevation={0}>
                    <MetricLabel>CPU</MetricLabel>
                    <MetricValue sx={{ fontSize: '1.4rem' }}>
                      {((clusterMetrics.summary.totalCpuMillicores || 0) / 1000).toFixed(1)}
                    </MetricValue>
                    <Typography variant="caption" color="textSecondary">
                      cores ({clusterMetrics.summary.avgCpuMillicores || 0}m avg)
                    </Typography>
                  </MetricCard>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard elevation={0}>
                    <MetricLabel>Restarts</MetricLabel>
                    <MetricValue sx={{ 
                      fontSize: '1.4rem', 
                      color: (clusterMetrics.summary.totalRestarts || 0) > 0 ? '#f59e0b' : '#22c55e' 
                    }}>
                      {clusterMetrics.summary.totalRestarts || 0}
                    </MetricValue>
                    <Typography variant="caption" color="textSecondary">
                      total across pods
                    </Typography>
                  </MetricCard>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard elevation={0}>
                    <MetricLabel>High Memory</MetricLabel>
                    <MetricValue sx={{ 
                      fontSize: '1.4rem', 
                      color: (clusterMetrics.summary.podsWithHighMemory || 0) > 0 ? '#ef4444' : '#22c55e' 
                    }}>
                      {clusterMetrics.summary.podsWithHighMemory || 0}
                    </MetricValue>
                    <Typography variant="caption" color="textSecondary">
                      pods &gt;80% limit
                    </Typography>
                  </MetricCard>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <MetricCard elevation={0}>
                    <MetricLabel>Network Out</MetricLabel>
                    <MetricValue sx={{ fontSize: '1.4rem' }}>
                      {formatBytesPerSec(clusterMetrics.summary.totalNetworkTxBytesPerSec || 0)}
                    </MetricValue>
                    <Typography variant="caption" color="textSecondary">
                      in: {formatBytesPerSec(clusterMetrics.summary.totalNetworkRxBytesPerSec || 0)}
                    </Typography>
                  </MetricCard>
                </Grid>
              </Grid>

              {/* Pod Memory Chart */}
              <ChartCard sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Memory Usage by Pod
                  </Typography>
                  <Typography variant="caption" color="textSecondary" gutterBottom sx={{ display: 'block', mb: 2 }}>
                    {clusterMetrics.querySource === 'mock' ? '🎭 Simulated data' : 'Data from Prometheus'} • Last updated: {new Date(clusterMetrics.timestamp).toLocaleTimeString()}
                  </Typography>
                  
                  <ResponsiveContainer width="100%" height={Math.max(200, clusterMetrics.pods.length * 40)}>
                    <LineChart
                      layout="vertical"
                      data={clusterMetrics.pods}
                      margin={{ top: 5, right: 30, left: 150, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis type="number" stroke="rgba(255,255,255,0.5)" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                      <YAxis
                        dataKey="podName"
                        type="category"
                        stroke="rgba(255,255,255,0.5)"
                        tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
                        width={140}
                      />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: 'rgba(15, 23, 42, 0.95)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: 8,
                        }}
                        formatter={(value: number) => [`${value} MB`, 'Memory']}
                      />
                      <Line
                        dataKey="memoryUsedMB"
                        stroke="#60a5fa"
                        strokeWidth={2}
                        dot={(props) => {
                          const { cx, cy, payload } = props;
                          const isCurrentPod = payload.podName === clusterMetrics.currentPod;
                          return (
                            <circle
                              cx={cx}
                              cy={cy}
                              r={isCurrentPod ? 8 : 5}
                              fill={isCurrentPod ? '#34d399' : '#60a5fa'}
                              stroke={isCurrentPod ? '#fff' : 'none'}
                              strokeWidth={isCurrentPod ? 2 : 0}
                            />
                          );
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  
                  {/* Legend */}
                  <Box display="flex" gap={3} mt={2} justifyContent="center">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#60a5fa' }} />
                      <Typography variant="caption" color="textSecondary">Other Pods</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#34d399', border: '2px solid #fff' }} />
                      <Typography variant="caption" color="textSecondary">Current Pod ({clusterMetrics.currentPod})</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </ChartCard>

              {/* Pod List Table - Enhanced */}
              <ChartCard>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    All Pods - Detailed View
                  </Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', textTransform: 'uppercase' }}>Pod Name</th>
                          <th style={{ textAlign: 'center', padding: '12px 8px', borderBottom: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', textTransform: 'uppercase' }}>Memory</th>
                          <th style={{ textAlign: 'center', padding: '12px 8px', borderBottom: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', textTransform: 'uppercase' }}>CPU</th>
                          <th style={{ textAlign: 'center', padding: '12px 8px', borderBottom: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', textTransform: 'uppercase' }}>Restarts</th>
                          <th style={{ textAlign: 'center', padding: '12px 8px', borderBottom: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', textTransform: 'uppercase' }}>Network</th>
                          <th style={{ textAlign: 'center', padding: '12px 8px', borderBottom: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', textTransform: 'uppercase' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clusterMetrics.pods.map((pod) => {
                          const memPercent = pod.memoryPercentUsed || 0;
                          const cpuPercent = pod.cpuPercentUsed || 0;
                          const isHighMemory = memPercent > 80;
                          const isHighCpu = cpuPercent > 80;
                          const hasRestarts = (pod.restartCount || 0) > 0;
                          
                          return (
                            <tr key={pod.podName} style={{ 
                              backgroundColor: pod.podName === clusterMetrics.currentPod 
                                ? 'rgba(52, 211, 153, 0.1)' 
                                : isHighMemory ? 'rgba(239, 68, 68, 0.05)' : 'transparent' 
                            }}>
                              <td style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  {pod.podName}
                                  {pod.podName === clusterMetrics.currentPod && (
                                    <Chip size="small" label="current" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: 'rgba(52, 211, 153, 0.2)', color: '#34d399' }} />
                                  )}
                                  {isHighMemory && (
                                    <Chip size="small" label="⚠️" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' }} />
                                  )}
                                </Box>
                              </td>
                              {/* Memory Column */}
                              <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'center' }}>
                                <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={memPercent}
                                      sx={{
                                        width: 50,
                                        height: 6,
                                        borderRadius: 3,
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                        '& .MuiLinearProgress-bar': {
                                          borderRadius: 3,
                                          backgroundColor: isHighMemory ? '#ef4444' : memPercent > 60 ? '#f59e0b' : '#60a5fa',
                                        },
                                      }}
                                    />
                                    <Typography variant="caption" fontFamily="monospace" sx={{ minWidth: 35 }}>
                                      {memPercent}%
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" color="textSecondary" fontSize="0.65rem">
                                    {pod.memoryUsedMB}/{pod.memoryLimitMB || '?'} MB
                                  </Typography>
                                </Box>
                              </td>
                              {/* CPU Column */}
                              <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'center' }}>
                                <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={cpuPercent}
                                      sx={{
                                        width: 50,
                                        height: 6,
                                        borderRadius: 3,
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                        '& .MuiLinearProgress-bar': {
                                          borderRadius: 3,
                                          backgroundColor: isHighCpu ? '#ef4444' : cpuPercent > 60 ? '#f59e0b' : '#f472b6',
                                        },
                                      }}
                                    />
                                    <Typography variant="caption" fontFamily="monospace" sx={{ minWidth: 35 }}>
                                      {cpuPercent}%
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" color="textSecondary" fontSize="0.65rem">
                                    {pod.cpuUsageMillicores || 0}m / {pod.cpuLimitMillicores || '?'}m
                                  </Typography>
                                </Box>
                              </td>
                              {/* Restarts Column */}
                              <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'center' }}>
                                <Typography 
                                  variant="body2" 
                                  fontFamily="monospace"
                                  sx={{ 
                                    color: hasRestarts ? (pod.restartCount! > 2 ? '#ef4444' : '#f59e0b') : '#22c55e',
                                    fontWeight: hasRestarts ? 600 : 400,
                                  }}
                                >
                                  {pod.restartCount || 0}
                                </Typography>
                              </td>
                              {/* Network Column */}
                              <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'center' }}>
                                <Box display="flex" flexDirection="column" alignItems="center" gap={0.25}>
                                  <Typography variant="caption" color="textSecondary" fontSize="0.65rem">
                                    ↑ {formatBytesPerSec(pod.networkTxBytesPerSec || 0)}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary" fontSize="0.65rem">
                                    ↓ {formatBytesPerSec(pod.networkRxBytesPerSec || 0)}
                                  </Typography>
                                </Box>
                              </td>
                              {/* Status Column */}
                              <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'center' }}>
                                <Chip
                                  size="small"
                                  label={pod.ready ? 'Ready' : 'Not Ready'}
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    backgroundColor: pod.ready ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                    color: pod.ready ? '#22c55e' : '#ef4444',
                                  }}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Box>
                  
                  {/* Legend */}
                  <Box display="flex" gap={3} mt={2} flexWrap="wrap">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: '#60a5fa' }} />
                      <Typography variant="caption" color="textSecondary">Memory &lt;60%</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: '#f59e0b' }} />
                      <Typography variant="caption" color="textSecondary">60-80%</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: '#ef4444' }} />
                      <Typography variant="caption" color="textSecondary">&gt;80% (Warning)</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ width: 12, height: 12, borderRadius: 1, backgroundColor: '#f472b6' }} />
                      <Typography variant="caption" color="textSecondary">CPU Usage</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </ChartCard>
            </>
          )}
        </Box>
      )}

      {/* Pod View - Quick Stats (only show in pod view) */}
      {viewMode === 'pod' && (
      <>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard elevation={0}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <MemoryIcon sx={{ color: CHART_COLORS.heapUsed, fontSize: 20 }} />
              <MetricLabel>Heap Used</MetricLabel>
            </Box>
            <MetricValue>{systemMetrics?.memory.heap.used || '-'}</MetricValue>
            <Box mt={1}>
              <LinearProgress
                variant="determinate"
                value={systemMetrics?.memory.heap.percentUsed || 0}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    background: `linear-gradient(90deg, ${CHART_COLORS.heapUsed}, ${CHART_COLORS.heapTotal})`,
                  },
                }}
              />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                {systemMetrics?.memory.heap.percentUsed || 0}% of {systemMetrics?.memory.v8HeapLimit || '-'}
              </Typography>
            </Box>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard elevation={0}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <StorageIcon sx={{ color: CHART_COLORS.rss, fontSize: 20 }} />
              <MetricLabel>RSS Memory</MetricLabel>
            </Box>
            <MetricValue>{systemMetrics?.memory.rss || '-'}</MetricValue>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              Total process memory
            </Typography>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard elevation={0}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <TimerIcon sx={{ color: '#f472b6', fontSize: 20 }} />
              <MetricLabel>Uptime</MetricLabel>
            </Box>
            <MetricValue sx={{ fontSize: '1.5rem' }}>{systemMetrics?.process.uptime || '-'}</MetricValue>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              PID: {systemMetrics?.process.pid || '-'}
            </Typography>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard elevation={0}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <SpeedIcon sx={{ color: CHART_COLORS.external, fontSize: 20 }} />
              <MetricLabel>Load Average</MetricLabel>
            </Box>
            <MetricValue sx={{ fontSize: '1.25rem' }}>
              {systemMetrics?.system.loadAverage.map((l) => l.toFixed(2)).join(' / ') || '-'}
            </MetricValue>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              {systemMetrics?.system.cpuCount || '-'} CPUs
            </Typography>
          </MetricCard>
        </Grid>
      </Grid>

      {/* Controls */}
      <ChartCard sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box display="flex" gap={2} alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={isPolling}
                    onChange={(e) => setIsPolling(e.target.checked)}
                    color="primary"
                  />
                }
                label="Live Updates (3s)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={autoSnapshot}
                    onChange={(e) => setAutoSnapshot(e.target.checked)}
                    color="primary"
                  />
                }
                label="Auto-Snapshot (10s)"
              />
            </Box>
            <Box display="flex" gap={1}>
              <ActionButton
                variant="outlined"
                size="small"
                startIcon={<PlayArrowIcon />}
                onClick={recordSnapshot}
              >
                Record Snapshot
              </ActionButton>
              <ActionButton
                variant="outlined"
                size="small"
                startIcon={<CleaningServicesIcon />}
                onClick={() => triggerGC(false)}
                disabled={!systemMetrics?.gc.available}
                color="warning"
              >
                Minor GC
              </ActionButton>
              <ActionButton
                variant="contained"
                size="small"
                startIcon={<CleaningServicesIcon />}
                onClick={() => triggerGC(true)}
                disabled={!systemMetrics?.gc.available}
                color="warning"
              >
                Full GC
              </ActionButton>
              <ActionButton
                variant="outlined"
                size="small"
                startIcon={<DeleteSweepIcon />}
                onClick={clearHistory}
                color="error"
              >
                Clear History
              </ActionButton>
            </Box>
          </Box>
          {!systemMetrics?.gc.available && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
              ⚠️ GC not available. Start server with --expose-gc flag to enable manual garbage collection.
            </Typography>
          )}
        </CardContent>
      </ChartCard>

      {/* Memory Chart */}
      <ChartCard sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Memory Over Time
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {memoryHistory.length} snapshots recorded
              </Typography>
            </Box>
            {trend && (
              <Box display="flex" alignItems="center" gap={1}>
                {trend.growth > 0 ? (
                  <TrendingUpIcon sx={{ color: '#ef4444' }} />
                ) : (
                  <TrendingDownIcon sx={{ color: '#22c55e' }} />
                )}
                <Typography
                  variant="body2"
                  sx={{ color: trend.growth > 0 ? '#ef4444' : '#22c55e', fontWeight: 600 }}
                >
                  {trend.growth > 0 ? '+' : ''}
                  {trend.growth} MB over {trend.durationMin} min
                </Typography>
              </Box>
            )}
          </Box>

          {memoryHistory.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="heapUsedGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.heapUsed} stopOpacity={0.4} />
                    <stop offset="95%" stopColor={CHART_COLORS.heapUsed} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="heapTotalGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.heapTotal} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS.heapTotal} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="rssGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.rss} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS.rss} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis
                  dataKey="time"
                  stroke="rgba(255,255,255,0.5)"
                  tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }}
                />
                <YAxis
                  stroke="rgba(255,255,255,0.5)"
                  tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }}
                  label={{
                    value: 'MB',
                    angle: -90,
                    position: 'insideLeft',
                    fill: 'rgba(255,255,255,0.6)',
                  }}
                />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: 'rgba(15, 23, 42, 0.95)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 8,
                  }}
                  labelStyle={{ color: '#fff' }}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="Heap Used"
                  stroke={CHART_COLORS.heapUsed}
                  fill="url(#heapUsedGradient)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="Heap Total"
                  stroke={CHART_COLORS.heapTotal}
                  fill="url(#heapTotalGradient)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="RSS"
                  stroke={CHART_COLORS.rss}
                  fill="url(#rssGradient)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height={300}
              flexDirection="column"
              gap={2}
            >
              <Typography color="textSecondary">No snapshots recorded yet</Typography>
              <ActionButton variant="outlined" onClick={recordSnapshot} startIcon={<PlayArrowIcon />}>
                Record First Snapshot
              </ActionButton>
            </Box>
          )}
        </CardContent>
      </ChartCard>

      {/* Heap Analysis Section */}
      <ChartCard sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <BugReportIcon sx={{ color: '#f472b6' }} />
              <Typography variant="h6" fontWeight={600}>
                Heap Analysis
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <ActionButton
                variant="outlined"
                size="small"
                startIcon={heapAnalysisLoading ? <CircularProgress size={16} /> : <BugReportIcon />}
                onClick={fetchHeapAnalysis}
                disabled={heapAnalysisLoading}
                sx={{ borderColor: '#f472b6', color: '#f472b6' }}
              >
                Analyze Heap
              </ActionButton>
              <ActionButton
                variant="contained"
                size="small"
                startIcon={heapSnapshotLoading ? <CircularProgress size={16} color="inherit" /> : <CameraAltIcon />}
                onClick={writeHeapSnapshot}
                disabled={heapSnapshotLoading}
                sx={{ backgroundColor: '#a78bfa', '&:hover': { backgroundColor: '#8b5cf6' } }}
              >
                Write Snapshot
              </ActionButton>
            </Box>
          </Box>

          {/* Snapshot Result */}
          {heapSnapshotResult && (
            <Alert 
              severity={heapSnapshotResult.success ? 'success' : 'error'} 
              sx={{ mb: 2 }}
              onClose={() => setHeapSnapshotResult(null)}
            >
              <Typography variant="body2" fontWeight={600}>{heapSnapshotResult.message}</Typography>
              {heapSnapshotResult.success && (
                <Box mt={1}>
                  <Typography variant="caption" display="block" fontFamily="monospace">
                    📁 {heapSnapshotResult.filepath} ({heapSnapshotResult.sizeMB} MB)
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {heapSnapshotResult.instructions}
                  </Typography>
                </Box>
              )}
            </Alert>
          )}

          {!heapAnalysis && !heapAnalysisLoading && (
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center" 
              py={4}
              flexDirection="column"
              gap={1}
            >
              <InfoIcon sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 40 }} />
              <Typography color="textSecondary">
                Click "Analyze Heap" to get detailed V8 heap breakdown and recommendations
              </Typography>
            </Box>
          )}

          {heapAnalysisLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={24} />
            </Box>
          )}

          {heapAnalysis && !heapAnalysisLoading && (
            <>
              {/* Memory Pressure Indicator */}
              <Box mb={3}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  <Typography variant="subtitle2" color="textSecondary">Memory Pressure:</Typography>
                  <Chip
                    size="small"
                    icon={
                      heapAnalysis.analysis.memoryPressure === 'critical' ? <WarningAmberIcon /> :
                      heapAnalysis.analysis.memoryPressure === 'high' ? <WarningAmberIcon /> :
                      <CheckCircleIcon />
                    }
                    label={heapAnalysis.analysis.memoryPressure.toUpperCase()}
                    sx={{
                      backgroundColor: 
                        heapAnalysis.analysis.memoryPressure === 'critical' ? 'rgba(239, 68, 68, 0.2)' :
                        heapAnalysis.analysis.memoryPressure === 'high' ? 'rgba(245, 158, 11, 0.2)' :
                        heapAnalysis.analysis.memoryPressure === 'medium' ? 'rgba(96, 165, 250, 0.2)' :
                        'rgba(34, 197, 94, 0.2)',
                      color: 
                        heapAnalysis.analysis.memoryPressure === 'critical' ? '#ef4444' :
                        heapAnalysis.analysis.memoryPressure === 'high' ? '#f59e0b' :
                        heapAnalysis.analysis.memoryPressure === 'medium' ? '#60a5fa' :
                        '#22c55e',
                      fontWeight: 600,
                    }}
                  />
                  <Typography variant="body2" fontFamily="monospace">
                    {heapAnalysis.heapStatistics.usedHeapSizeMB} MB / {heapAnalysis.heapStatistics.heapSizeLimitMB} MB 
                    ({heapAnalysis.heapStatistics.utilizationPercent}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={heapAnalysis.heapStatistics.utilizationPercent}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      backgroundColor: 
                        heapAnalysis.analysis.memoryPressure === 'critical' ? '#ef4444' :
                        heapAnalysis.analysis.memoryPressure === 'high' ? '#f59e0b' :
                        heapAnalysis.analysis.memoryPressure === 'medium' ? '#60a5fa' :
                        '#22c55e',
                    },
                  }}
                />
              </Box>

              {/* Issues and Recommendations */}
              {(heapAnalysis.analysis.potentialIssues.length > 0 || heapAnalysis.analysis.recommendations.length > 0) && (
                <Box mb={3}>
                  {heapAnalysis.analysis.potentialIssues.length > 0 && (
                    <Alert 
                      severity="warning" 
                      sx={{ mb: 1, backgroundColor: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)' }}
                    >
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>⚠️ Potential Issues</Typography>
                      {heapAnalysis.analysis.potentialIssues.map((issue, i) => (
                        <Typography key={i} variant="body2" sx={{ ml: 2 }}>• {issue}</Typography>
                      ))}
                    </Alert>
                  )}
                  {heapAnalysis.analysis.recommendations.length > 0 && (
                    <Alert 
                      severity="info" 
                      sx={{ backgroundColor: 'rgba(96, 165, 250, 0.1)', border: '1px solid rgba(96, 165, 250, 0.3)' }}
                    >
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>💡 Recommendations</Typography>
                      {heapAnalysis.analysis.recommendations.map((rec, i) => (
                        <Typography key={i} variant="body2" sx={{ ml: 2 }}>• {rec}</Typography>
                      ))}
                    </Alert>
                  )}
                </Box>
              )}

              {/* Heap Spaces Breakdown */}
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                V8 Heap Spaces Breakdown
              </Typography>
              <Grid container spacing={1} mb={2}>
                {heapAnalysis.heapSpaces
                  .filter(space => space.spaceSizeMB > 0)
                  .sort((a, b) => b.spaceUsedSizeMB - a.spaceUsedSizeMB)
                  .map(space => (
                    <Grid item xs={12} sm={6} md={4} key={space.spaceName}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          backgroundColor: 'rgba(30, 41, 59, 0.6)',
                          border: '1px solid rgba(255,255,255,0.1)',
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="caption" fontFamily="monospace" sx={{ color: HEAP_SPACE_COLORS[space.spaceName] || '#60a5fa' }}>
                            {space.spaceName.replace(/_/g, ' ')}
                          </Typography>
                          <Typography variant="caption" fontFamily="monospace">
                            {space.utilizationPercent}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={space.utilizationPercent}
                          sx={{
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 2,
                              backgroundColor: HEAP_SPACE_COLORS[space.spaceName] || '#60a5fa',
                            },
                          }}
                        />
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                          {space.spaceUsedSizeMB} / {space.spaceSizeMB} MB
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
              </Grid>

              {/* Detailed Stats */}
              <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">Native Contexts</Typography>
                  <Typography variant="body2" fontFamily="monospace">{heapAnalysis.heapStatistics.numberOfNativeContexts}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">Detached Contexts</Typography>
                  <Typography 
                    variant="body2" 
                    fontFamily="monospace"
                    sx={{ color: heapAnalysis.heapStatistics.numberOfDetachedContexts > 0 ? '#f59e0b' : 'inherit' }}
                  >
                    {heapAnalysis.heapStatistics.numberOfDetachedContexts}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">Malloced Memory</Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {Math.round(heapAnalysis.heapStatistics.mallocedMemory / 1024 / 1024)} MB
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">Peak Malloced</Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {Math.round(heapAnalysis.heapStatistics.peakMallocedMemory / 1024 / 1024)} MB
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">External Memory</Typography>
                  <Typography variant="body2" fontFamily="monospace">{heapAnalysis.processMemory.externalMB} MB</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">ArrayBuffers</Typography>
                  <Typography variant="body2" fontFamily="monospace">{heapAnalysis.processMemory.arrayBuffersMB} MB</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">Available Heap</Typography>
                  <Typography variant="body2" fontFamily="monospace">{heapAnalysis.heapStatistics.availableMB} MB</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">Last Analyzed</Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {new Date(heapAnalysis.timestamp).toLocaleTimeString()}
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}
        </CardContent>
      </ChartCard>

      {/* System Info */}
      <Grid container spacing={2}>
        {/* Instance/K8s Information - only show if K8s environment detected */}
        {isKubernetes && (
          <Grid item xs={12}>
            <ChartCard>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  🐳 Kubernetes Instance
                </Typography>
                <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.1)' }} />
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">Pod Name</Typography>
                    <Typography variant="body2" fontFamily="monospace" sx={{ color: '#60a5fa' }}>
                      {systemMetrics?.instance?.podName || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">Namespace</Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {systemMetrics?.instance?.namespace || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">Node</Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {systemMetrics?.instance?.nodeName || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">Container ID</Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {systemMetrics?.instance?.containerId || '-'}
                    </Typography>
                  </Grid>
                </Grid>
                <Alert severity="info" sx={{ mt: 2, backgroundColor: 'rgba(96, 165, 250, 0.1)' }}>
                  <Typography variant="body2">
                    <strong>Multi-Pod Environment:</strong> Each API call may be routed to a different pod. 
                    For cluster-wide metrics, use Prometheus/Grafana or the Kubernetes Metrics Server.
                  </Typography>
                </Alert>
              </CardContent>
            </ChartCard>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <ChartCard>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Process Information
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.1)' }} />
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Node Version</Typography>
                  <Typography variant="body2" fontFamily="monospace">{systemMetrics?.process.nodeVersion}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Platform</Typography>
                  <Typography variant="body2" fontFamily="monospace">{systemMetrics?.process.platform} ({systemMetrics?.process.arch})</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">V8 Heap Limit</Typography>
                  <Typography variant="body2" fontFamily="monospace">{systemMetrics?.memory.v8HeapLimit}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">External Memory</Typography>
                  <Typography variant="body2" fontFamily="monospace">{systemMetrics?.memory.external}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                System Information
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.1)' }} />
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Total Memory</Typography>
                  <Typography variant="body2" fontFamily="monospace">{systemMetrics?.system.totalMemory}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Free Memory</Typography>
                  <Typography variant="body2" fontFamily="monospace">{systemMetrics?.system.freeMemory}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">CPU Count</Typography>
                  <Typography variant="body2" fontFamily="monospace">{systemMetrics?.system.cpuCount} cores</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Last Manual GC</Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {systemMetrics?.gc.lastManualGC
                      ? new Date(systemMetrics.gc.lastManualGC).toLocaleTimeString()
                      : 'Never'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>
      </>
      )}
      {/* End of Pod View conditional */}
    </Box>
  );
};

export default EnvironmentMonitor;

